import { StateContext } from '@store/store';
import { useContext, useMemo } from 'react';
import { AddonSubscriptionPlan, UsageQuota } from 'src/types/pricing.types';

export const creditsRange = [0, 1, 2, 3, 5, 10, 20, 30, 60, 120];

export function useAiCredits({
  isYearlyDuration,
  planKey,
  addonPlan,
}: {
  isYearlyDuration: boolean;
  planKey: string;
  addonPlan?: AddonSubscriptionPlan;
}) {
  const globalState = useContext(StateContext);
  const { aiCreditsCounter } = globalState;

  const aiCreditsPrice = useMemo(() => {
    if (!addonPlan) {
      return 0;
    }

    if (isYearlyDuration) {
      return (
        creditsRange[aiCreditsCounter[planKey]] *
        (addonPlan?.yearly?.amount / 12)
      );
    } else {
      return (
        creditsRange[aiCreditsCounter[planKey]] * addonPlan?.monthly?.amount
      );
    }
  }, [isYearlyDuration, aiCreditsCounter, planKey, addonPlan]);

  return {
    aiCredits:
      creditsRange[aiCreditsCounter[planKey]] *
      (addonPlan?.usage_quota?.ai_credits_v2 || 0),
    aiCreditsBoost: creditsRange[aiCreditsCounter[planKey]],
    aiCreditsPrice,
  };
}

export const getSeatsFromCounter = (counter: number) => {
  return creditsRange[counter];
};

export const getAiCreditsFromCounter = (
  counter: number,
  usageQuota: UsageQuota,
) => {
  return creditsRange[counter] * (usageQuota?.ai_credits_v2 || 0);
};

export const getAiCreditsPrice = ({
  planKey,
  isYearlyDuration,
  globalState,
  addonPlan,
}: {
  planKey: string;
  isYearlyDuration: boolean;
  globalState: any;
  addonPlan?: AddonSubscriptionPlan;
}) => {
  const { aiCreditsCounter } = globalState;

  let aiCreditsPrice = 0;

  if (!addonPlan) {
    return 0;
  }

  if (isYearlyDuration) {
    aiCreditsPrice =
      creditsRange[aiCreditsCounter[planKey]] *
      (addonPlan?.yearly?.amount || 0 / 12);
  } else {
    aiCreditsPrice =
      creditsRange[aiCreditsCounter[planKey]] *
      (addonPlan?.monthly?.amount || 0);
  }
  return aiCreditsPrice;
};

export const convertGenerativeCreditsToTime = (credits: number): string => {
  if (typeof credits !== 'number') {
    return 'No';
  }

  if (credits < 199) {
    return `${credits} sec`;
  }

  return `${Math.floor(credits / 60)} min`;
};

export const getPricingValueFromCredits = (
  credits: number,
  pricing: string,
) => {
  switch (pricing) {
    case 'ai_credits': {
      return Math.floor(credits / 0.333);
    }
    case 'ugc_ads':
      return Math.floor(credits / 5) < 1 ? 0 : Math.floor(credits / 5);
    case 'generative_video':
      return Math.floor(credits / 0.333) < 1 ? 1 : Math.floor(credits / 0.333);
    case 'stock_ads':
      return Math.floor(credits / 5) < 1 ? 1 : Math.floor(credits / 5);
    case 'stock_video':
      return Math.floor(credits / 0.5) < 1 ? 1 : Math.floor(credits / 0.5);
    case 'generative_image':
      return Math.floor(credits / 0.22) < 1 ? 1 : Math.floor(credits / 0.2);
    case 'generative_clip':
      return Math.floor(credits / 0.333) < 1 ? 1 : Math.floor(credits / 0.333);
    default:
      return 0;
  }
};

export function secondsToMinutes(seconds: number): number {
  const minutes = seconds / 60;
  return Math.trunc(minutes);
}
